import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState, type CSSProperties } from "react";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, MessageCircle } from "lucide-react";
import { AdminAccessGate } from "@/components/admin-access-gate";
import {
  useAsaasIntegration,
  useUpdateAsaasIntegration,
  useWhatsappIntegration,
  useConnectWhatsappIntegration,
  useDisconnectWhatsappIntegration,
} from "@/hooks/queries";
import { GpsDriveApiError } from "@/lib/api/gpsdrive-client";
import asaasLogoUrl from "@/assets/asaas-logo.svg";

export const Route = createFileRoute("/admin/integrations")({
  component: () => (
    <AdminAccessGate action="conta_integracoes">
      <IntegrationsPage />
    </AdminAccessGate>
  ),
  head: () => ({ meta: [{ title: "Integrações — Administração — Wp Rastreamento" }] }),
});

// Contrato é uma seção separada (ver admin.contract.tsx) — não faz parte
// desta tela, mesmo os dois tendo nascido juntos aqui no início.
function IntegrationsPage() {
  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Integrações</h1>
        <p className="text-sm text-muted-foreground">Conecte serviços externos usados pela empresa.</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <AsaasIntegrationCard />
        <WhatsappIntegrationCard />
      </div>
    </div>
  );
}

// Logo oficial baixado direto de cdn-boto.asaas.com (svg com fill="white",
// pensado pra ficar sobre um fundo escuro — daí o tile abaixo, não é um
// wordmark colorido solto).
const ASAAS_TILE_BG = "#0B1B33";

function AsaasIntegrationCard() {
  const { data: status, isLoading } = useAsaasIntegration();
  const updateIntegration = useUpdateAsaasIntegration();
  const [open, setOpen] = useState(false);

  const [apiKey, setApiKey] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [dueDays, setDueDays] = useState("5");
  const [suspensionDays, setSuspensionDays] = useState("");
  // Trava extra pedida 2026-09-12, além de type="text"+mascaramento: o
  // campo nasce readOnly e só vira editável no primeiro clique/foco do
  // usuário (handleUnlockApiKey abaixo). Nenhum gerenciador de senha —
  // Chrome, LastPass, 1Password, extensões — preenche um input readOnly,
  // então isso fecha a porta por completo, não só reduz a chance: nada
  // entra nesse campo a não ser o que o usuário digitar/colar depois de
  // clicar nele de propósito.
  const [apiKeyLocked, setApiKeyLocked] = useState(true);

  // Reseta o formulário com o valor do servidor toda vez que o dialog abre
  // (nunca no meio de uma edição em andamento).
  useEffect(() => {
    if (!open) return;
    setApiKey("");
    setApiKeyLocked(true);
    setEnabled(status?.enabled ?? false);
    setDueDays(String(status?.dueDays ?? 5));
    setSuspensionDays(status?.suspensionDays != null ? String(status.suspensionDays) : "");
  }, [open, status]);

  const handleUnlockApiKey = () => setApiKeyLocked(false);

  const active = Boolean(status?.enabled && status.hasApiKey);

  const handleSave = async () => {
    const dueDaysNum = Number(dueDays);
    if (!Number.isInteger(dueDaysNum) || dueDaysNum < 1) {
      toast.error("Dias para vencimento deve ser um número inteiro maior que zero");
      return;
    }
    let suspensionDaysNum: number | null = null;
    if (suspensionDays.trim() !== "") {
      suspensionDaysNum = Number(suspensionDays);
      if (!Number.isInteger(suspensionDaysNum) || suspensionDaysNum < 1) {
        toast.error("Dias para suspensão deve ser um número inteiro maior que zero");
        return;
      }
    }

    try {
      await updateIntegration.mutateAsync({
        apiKey: apiKey.trim() || undefined,
        enabled,
        dueDays: dueDaysNum,
        suspensionDays: suspensionDaysNum,
      });
      toast.success("Integração com o Asaas atualizada");
      setOpen(false);
    } catch (err) {
      toast.error(err instanceof GpsDriveApiError ? err.message : "Falha ao salvar a integração");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Card className="h-full cursor-pointer transition-colors hover:border-primary/40 hover:bg-accent/40">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div
                className="flex h-10 w-16 shrink-0 items-center justify-center rounded-md px-2"
                style={{ backgroundColor: ASAAS_TILE_BG }}
              >
                <img src={asaasLogoUrl} alt="Asaas" className="h-3 w-full object-contain" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base">Asaas</CardTitle>
                  {active && (
                    <Badge className="border-transparent bg-emerald-500 text-white hover:bg-emerald-500">
                      Ativo
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <CardDescription>Cobrança automática e sincronização de clientes.</CardDescription>
          </CardHeader>
        </Card>
      </DialogTrigger>

      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Integração com o Asaas</DialogTitle>
          <DialogDescription>
            Ao ativar, todo cliente salvo na aba Cliente/Financeiro (Editar Usuário) é
            automaticamente criado/atualizado no Asaas.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="asaas-api-key">Chave de API</Label>
            <Input
              id="asaas-api-key"
              // NUNCA type="password" aqui (bug reportado 2026-09-12): Chrome
              // trata qualquer input type=password como campo de LOGIN e,
              // sem formulário nem campo de usuário por perto, ainda assim
              // oferece a senha salva do próprio site (heurística de "único
              // campo de senha da página") — o operador via esse valor
              // aparecer sozinho no campo e achava que a chave real tinha
              // sido alterada; pior, se salvasse sem perceber, a senha do
              // Chrome IA de verdade sobrescrever a chave real do Asaas (ver
              // handleSave: apiKey não-vazio sempre vira o novo valor).
              // type="text" + mascaramento via CSS (-webkit-text-security)
              // mantém o visual de senha sem acionar o gerenciador de senha.
              // autoComplete="off" sozinho não basta pro Chrome (ele ignora
              // em campos de senha), mas aqui nem chega a ser um — os
              // data-*-ignore cobrem 1Password/LastPass/Bitwarden, que
              // seguem esses atributos à parte do heurístico do Chrome.
              type="text"
              inputMode="text"
              style={{ WebkitTextSecurity: "disc" } as CSSProperties}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-lpignore="true"
              data-1p-ignore="true"
              data-bwignore="true"
              readOnly={apiKeyLocked}
              onFocus={handleUnlockApiKey}
              onMouseDown={handleUnlockApiKey}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={status?.hasApiKey ? "•••••••••••••••• (chave configurada)" : "$aact_..."}
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground">
              Encontrada em Asaas → Integrações → API. Deixe em branco para manter a chave já
              salva. O campo só aceita edição depois que você clicar nele.
            </p>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Integração ativa</p>
              <p className="text-xs text-muted-foreground">
                Desligar não apaga a chave salva, só para de criar clientes no Asaas.
              </p>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} disabled={isLoading} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="asaas-due-days">Dias para vencimento</Label>
            <Input
              id="asaas-due-days"
              type="number"
              min={1}
              max={365}
              value={dueDays}
              onChange={(e) => setDueDays(e.target.value)}
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground">
              Prazo padrão sugerido ao gerar uma cobrança nova.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="asaas-suspension-days">Dias para suspensão da conta do cliente</Label>
            <Input
              id="asaas-suspension-days"
              type="number"
              min={1}
              max={365}
              value={suspensionDays}
              onChange={(e) => setSuspensionDays(e.target.value)}
              placeholder="Deixe em branco para não suspender automaticamente"
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground">
              Dias de atraso após o vencimento até suspender o acesso do cliente no app (tela de
              cobrança com PIX). Some sozinho assim que a fatura é paga. Deixe em branco pra nunca
              suspender ninguém.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={handleSave} disabled={updateIntegration.isPending || isLoading}>
            {updateIntegration.isPending ? "Salvando…" : "Salvar alterações"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const WHATSAPP_TILE_BG = "#25D366";

// Diferente do Asaas: conta uazapi CENTRALIZADA (admintoken nosso no
// backend) — a empresa nunca digita token nenhum, só clica "Conectar" e
// escaneia o QR code com o WhatsApp dela (ver
// gpsdrive-whatsapp-integration.service.ts). `pollWhileConnecting` liga
// o refetch automático só enquanto o dialog está aberto E ainda não
// conectou — nunca fica batendo no backend com a tela fechada.
function WhatsappIntegrationCard() {
  const [open, setOpen] = useState(false);
  const [qrcode, setQrcode] = useState<string | null>(null);
  const { data: status, isLoading } = useWhatsappIntegration({
    pollWhileConnecting: open && qrcode !== null,
  });
  const connect = useConnectWhatsappIntegration();
  const disconnect = useDisconnectWhatsappIntegration();

  const connected = Boolean(status?.connected);
  const available = status?.available ?? true;

  // Assim que o polling confirma conectado, esconde o QR code sozinho —
  // sem exigir clique nenhum do usuário.
  useEffect(() => {
    if (connected) setQrcode(null);
  }, [connected]);

  useEffect(() => {
    if (!open) setQrcode(null);
  }, [open]);

  const handleConnect = async () => {
    try {
      const result = await connect.mutateAsync();
      if (result.connected) {
        toast.success("WhatsApp conectado");
      } else if (result.qrcode) {
        setQrcode(result.qrcode);
      } else {
        toast.error("A uazapi não retornou um QR code — tente novamente");
      }
    } catch (err) {
      toast.error(err instanceof GpsDriveApiError ? err.message : "Falha ao iniciar conexão com o WhatsApp");
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnect.mutateAsync();
      toast.success("WhatsApp desconectado");
    } catch (err) {
      toast.error(err instanceof GpsDriveApiError ? err.message : "Falha ao desconectar o WhatsApp");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Card className="h-full cursor-pointer transition-colors hover:border-primary/40 hover:bg-accent/40">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md"
                style={{ backgroundColor: WHATSAPP_TILE_BG }}
              >
                <MessageCircle className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base">WhatsApp</CardTitle>
                  {connected && (
                    <Badge className="border-transparent bg-emerald-500 text-white hover:bg-emerald-500">
                      Ativo
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <CardDescription>
              Boas-vindas automática e avisos de veículo offline para o cliente.
            </CardDescription>
          </CardHeader>
        </Card>
      </DialogTrigger>

      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Integração com o WhatsApp</DialogTitle>
          <DialogDescription>
            Usado para a Mensagem de Boas-Vindas (enviada quando o cliente conclui a assinatura pelo
            link de autoatendimento) e para os avisos de Falha de Comunicação — ambos configuráveis
            em Administração &gt; Comunicação.
          </DialogDescription>
        </DialogHeader>

        {!available ? (
          <p className="text-sm text-muted-foreground">
            Integração indisponível nesta implantação — fale com o suporte GPS Drive.
          </p>
        ) : isLoading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : connected ? (
          <div className="space-y-4">
            <p className="text-sm">
              Conectado{status?.phone ? ` como ` : ""}
              {status?.phone && <span className="font-medium">{status.phone}</span>}.
            </p>
            {status?.external ? (
              // Instância compartilhada vinculada pelo Super Master — a
              // empresa não desconecta (derrubaria o outro sistema).
              <p className="text-xs text-muted-foreground">
                Número configurado pelo suporte GPS Drive — nada a fazer por aqui.
              </p>
            ) : (
              <Button variant="destructive" onClick={handleDisconnect} disabled={disconnect.isPending}>
                {disconnect.isPending ? "Desconectando…" : "Desconectar"}
              </Button>
            )}
          </div>
        ) : qrcode ? (
          <div className="flex flex-col items-center gap-3">
            <img
              src={qrcode.startsWith("data:") ? qrcode : `data:image/png;base64,${qrcode}`}
              alt="QR code do WhatsApp"
              className="h-56 w-56 rounded-md border border-border"
            />
            <p className="text-center text-xs text-muted-foreground">
              Abra o WhatsApp Business no celular da empresa → Aparelhos conectados → Conectar um
              aparelho → escaneie este código.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Nenhum WhatsApp conectado ainda. Clique em conectar e escaneie o QR code com o
              WhatsApp Business da empresa.
            </p>
            <Button onClick={handleConnect} disabled={connect.isPending}>
              {connect.isPending ? "Gerando QR code…" : "Conectar"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
